import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { API_URL } from '..';

const ViewWorkforceLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [workforce, setWorkforce] = useState({
        fullName: '',
        address: '',
        postcode: '',
        phoneNumber: '',
        email: '',
        userImage: '',
        status: ''
    });
    const [message, setMessage] = useState({
        status: false,
        reason: '',
        desc: ''
    });
    const [isLoading, setIsLoading] = useState(true);

    // Fetch workforce data
    useEffect(() => {
        const fetchWorkforce = async () => {
            try {
                const response = await fetch(`${API_URL}/api/workforce/single/${id}`);
                if (response.status === 200) {
                    const data = await response.json();
                    setWorkforce({
                        fullName: data.fullName || '',
                        address: data.address || '',
                        postcode: data.postcode || '',
                        phoneNumber: data.phoneNumber || '',
                        email: data.email || '',
                        userImage: data.userImage ? `${API_URL}${data.userImage}` : '',
                        status: data.status || 'active'
                    });
                } else {
                    throw new Error('Failed to fetch workforce data');
                }
            } catch (error) {
                setMessage({ status: true, reason: 'error', desc: `Error: ${error.message}` });
                setTimeout(() => navigate('/workforce'), 2000);
            } finally {
                setIsLoading(false);
            }
        };
        fetchWorkforce();
    }, [id, navigate]);

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-body p-24">
                <style>
                    {`
                        .image-preview {
                            width: 150px;
                            height: 150px;
                            object-fit: cover;
                            border-radius: 8px;
                            border: 1px solid #ced4da;
                        }
                        .detail-label {
                            font-weight: 600;
                            color: #1a1a1a;
                            margin-bottom: 8px;
                        }
                        .detail-value {
                            color: #4a4a4a;
                            margin-bottom: 20px;
                            padding: 10px;
                            background: #f8f9fa;
                            border-radius: 8px;
                        }
                        .status-badge {
                            padding: 6px 12px;
                            border-radius: 4px;
                            font-size: 14px;
                        }
                        .status-active {
                            background-color: #28a745;
                            color: white;
                        }
                        .status-inactive {
                            background-color: #dc3545;
                            color: white;
                        }
                    `}
                </style>
                <div className="row justify-content-center">
                    <div className="col-xxl-6 col-xl-8 col-lg-10">
                        <div className="card border">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center mb-24">
                                    <h5 className="card-title mb-0">Workforce Details</h5>
                                    <button
                                        className="btn btn-outline-primary radius-8 px-20 py-8"
                                        onClick={() => navigate('/workforce')}
                                    >
                                        <Icon icon="material-symbols:arrow-back" className="me-1" />
                                        Back to List
                                    </button>
                                </div>

                                {/* Message Alert */}
                                {message.status && (
                                    <div className={`alert alert-${message.reason === 'error' ? 'danger' : 'success'} alert-dismissible fade show mb-20`} role="alert">
                                        {message.desc}
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setMessage({ ...message, status: false })}
                                            aria-label="Close"
                                        ></button>
                                    </div>
                                )}

                                {/* Loading State */}
                                {isLoading ? (
                                    <div className="text-center">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="mb-20">
                                            <label className="detail-label">User Image</label>
                                            <div className="image-preview">
                                                {workforce.userImage ? (
                                                    <img src={workforce.userImage} alt="Workforce" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f8f9fa' }}>
                                                        No Image
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="detail-label">Full Name</label>
                                            <div className="detail-value">{workforce.fullName || 'N/A'}</div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="detail-label">Address</label>
                                            <div className="detail-value">{workforce.address || 'N/A'}</div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="detail-label">Postcode</label>
                                            <div className="detail-value">{workforce.postcode || 'N/A'}</div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="detail-label">Phone Number</label>
                                            <div className="detail-value">{workforce.phoneNumber || 'N/A'}</div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="detail-label">Email</label>
                                            <div className="detail-value">{workforce.email || 'N/A'}</div>
                                        </div>
                                        <div className="mb-20">
                                            <label className="detail-label">Status</label>
                                            <div className={`status-badge status-${workforce.status}`}>
                                                {workforce.status.charAt(0).toUpperCase() + workforce.status.slice(1)}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewWorkforceLayer;